// @ts-nocheck

import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";

// This file provides utilities for hydrating "islands" with React components.
// Islands are DOM nodes with a `data-component` attribute that specifies the
// name of the component to load. This allows us to render React within our
// CMS pages and blocks.

// Map island name -> dynamic import (code-splitting)
// Dynamic imports are the mechanism that allows us to load components on demand
const registry = {
  FAQBlock: () => import("../components/blocks/FAQBlock/FAQBlock.jsx"),
  StandardDetail: () =>
    import("../components/blocks/InformationBlock/StandardDetail/StandardDetail.jsx"),
  MultiLocationDetail: () =>
    import("../components/blocks/InformationBlock/MultiLocationDetail/MultiLocationDetail.jsx"),
  FinancialAssistanceBlock: () =>
    import("../components/blocks/FinancialAssistanceBlock/FinancialAssistanceBlock.jsx"),
  AutoCompleteDemo: () => import("../components/demo/AutoCompleteDemo.jsx"),
  SectionedInputDemo: () => import("../components/demo/SectionedInputDemo.jsx"),
  TextAreaDemo: () => import("../components/demo/TextAreaDemo.jsx"),
  DropdownDemo: () => import("../components/demo/DropdownDemo.jsx"),
  SliderInputDemo: () => import("../components/demo/SliderInputDemo.jsx"),
  StepperInputDemo: () => import("../components/demo/StepperInputDemo.jsx"),
  ScheduleOnlineBlock: () =>
    import("../components/blocks/ScheduleOnlineBlock/ScheduleOnlineBlock.jsx"),
  PracticeProviderBlock: () =>
    import("../components/blocks/PracticeProviderBlock/PracticeProviderBlock.jsx"),
  HomeFADBlock: () => import("../components/blocks/HomeFADBlock/HomeFADBlock.jsx"),
  StepIndicatorDemo: () => import("../components/demo/StepIndicatorDemo.jsx"),
  ScheduleFlow: () => import("../components/features/ScheduleFlow/ScheduleFlow.jsx"),
};

const roots = new WeakMap();

// Allow per-island opt-out via data-strict="false"
function isStrict(node) {
  const v = node.getAttribute("data-strict");
  return v == null || v === "true";
}

// Read props from `data-props` attribute or a sibling <script> tag.
// The latter is useful for large props that would be unwieldy in an attribute.
function readProps(node) {
  const json = node.dataset.props;
  if (json) return JSON.parse(json);
  const id = node.id;
  if (id) {
    const script = document.querySelector(
      `script[type="application/json"][data-props-for="${CSS.escape(id)}"]`,
    );
    if (script?.textContent) return JSON.parse(script.textContent);
  }
  return {};
}

export async function mount(node) {
  if (roots.has(node)) return;
  const name = node.dataset.component;
  const loader = name && registry[name];
  if (!loader) {
    if (import.meta.env.DEV) console.warn(`[islands] Missing component for "${name}"`);
    return;
  }
  const { default: Component } = await loader();
  const props = readProps(node);
  const root = createRoot(node);
  roots.set(node, root);
  // React 18/19 render API + Strict Mode per island
  const element = isStrict(node) ? (
    <StrictMode>
      <Component {...props} />
    </StrictMode>
  ) : (
    <Component {...props} />
  );
  root.render(element);
}

export function unmount(node) {
  const root = roots.get(node);
  if (root) {
    root.unmount();
    roots.delete(node);
  }
}

export function findIslands(scope = document) {
  return Array.from(scope.querySelectorAll("[data-component]"));
}
